import { apiClient } from "@/shared/services/api";
import type { ApiResponse } from "@/features/auth/services/authApi";

// ─── Types ──────────────────────────────────────────────────────────────────

export interface Sowing {
  id: string;
  sowingInstructionId: string;
  instructionNumber?: string | null;
  sowingDate: string;
  traysSown: number;
  germinationChamberId?: string | null;
  germinationChamberName?: string | null;
  sownByUserId?: string | null;
  sownByUserName?: string | null;
  requisitionId?: string | null;
  requisitionNumber?: string | null;
  /** SowingStatus enum string ("Planned", "Sown", etc.). */
  status: string;
  notes?: string | null;
  /** Aggregate: trays germinated across all germination waves. */
  traysGerminated: number;
  traysDiscarded: number;
  traysRemaining: number;
  createdAt: string;
  updatedAt?: string | null;

  /** Server-computed: true when traysGerminated > 0. Gates amend/withdraw/reparent. */
  hasGermination: boolean;

  // Parent SowingInstruction context (read-only, populated by the amendment service):
  cropTypeName?: string | null;
  variety?: string | null;
  scheduledStartDate?: string | null;
  scheduledEndDate?: string | null;
  /** SeedSource enum (1 = CompanyInventory, 2 = CustomerProvided). */
  seedSource?: number | null;
  seedBatchNumber?: string | null;
  orderNumber?: string | null;
  customerName?: string | null;
}

export type AmendmentAction = 0 | 1 | 2;
export const AMENDMENT_ACTION = {
  FieldEdit: 0,
  Withdraw: 1,
  Reparent: 2,
} as const;

export interface SowingAmendment {
  id: string;
  sowingId: string;
  action: AmendmentAction;
  amendedByUserId: string;
  amendedByUserName?: string | null;
  amendedAt: string;
  reason?: string | null;
  priorSnapshot: string;
  newSnapshot: string;
  originalSowingInstructionId?: string | null;
  newSowingInstructionId?: string | null;
}

export interface AmendSowingRequest {
  traysSown?: number;
  sowingDate?: string;
  germinationChamberId?: string | null;
  notes?: string | null;
  reason?: string | null;
}

export interface WithdrawSowingRequest {
  reason: string;
}

export interface ReparentSowingRequest {
  newSowingInstructionId: string;
  reason: string;
}

// ─── API ────────────────────────────────────────────────────────────────────

export const sowingsApi = {
  amend: async (
    sowingId: string,
    payload: AmendSowingRequest
  ): Promise<Sowing> => {
    const { data } = await apiClient.patch<ApiResponse<Sowing>>(
      `/Sowings/${sowingId}`,
      payload
    );
    return data.data;
  },

  withdraw: async (
    sowingId: string,
    payload: WithdrawSowingRequest
  ): Promise<boolean> => {
    const { data } = await apiClient.post<ApiResponse<boolean>>(
      `/Sowings/${sowingId}/withdraw`,
      payload
    );
    return data.data;
  },

  reparent: async (
    sowingId: string,
    payload: ReparentSowingRequest
  ): Promise<Sowing> => {
    const { data } = await apiClient.post<ApiResponse<Sowing>>(
      `/Sowings/${sowingId}/reparent`,
      payload
    );
    return data.data;
  },

  getAmendmentsForSowing: async (
    sowingId: string
  ): Promise<SowingAmendment[]> => {
    const { data } = await apiClient.get<ApiResponse<SowingAmendment[]>>(
      `/Sowings/${sowingId}/amendments`
    );
    return data.data;
  },

  getByInstruction: async (instructionId: string): Promise<Sowing[]> => {
    const { data } = await apiClient.get<ApiResponse<Sowing[]>>(
      `/Sowings/instruction/${instructionId}`
    );
    return data.data;
  },

  getAmendmentsForInstruction: async (
    instructionId: string
  ): Promise<SowingAmendment[]> => {
    const { data } = await apiClient.get<ApiResponse<SowingAmendment[]>>(
      `/Sowings/instruction/${instructionId}/amendments`
    );
    return data.data;
  },
};
